/**
 * Returns the first non-colliding "<base> - <copyToken>",
 * "<base> - <copyToken> 2", "<base> - <copyToken> 3", … given a list of
 * the user's existing playlist names.
 *
 * Used by the Community library's "Make a copy" / Duplicate action.
 * Comparison is case-insensitive and trim-normalized; the original
 * `baseName` and `copyToken` casing is preserved in the output.
 *
 * Distinct from `resolveNonCollidingPlaylistName` (in
 * `playlist-name-utils.ts`) because the duplicate flow's pattern
 * inserts a token (typically "Copy") between the base name and the
 * numeric suffix, e.g. "Cooking - Copy 2", whereas the user-input
 * flows append the suffix directly to the typed text.
 *
 * Without this walk, a repeat-click of "Make a copy" today silently
 * merges items into the existing copy via the backend's
 * `findOrCreatePlaylist` dedupe (a latent bug). The walk also future-
 * proofs against an upcoming backend change that will throw on
 * duplicate names instead of dedupe.
 */
export default function nextAvailableCopyName (
  baseName: string,
  copyToken: string,
  existingNames: ReadonlyArray<string>,
): string {
  const norm = (s: string): string => String(s).trim().toLowerCase()
  const taken = new Set<string>()
  existingNames.forEach((n) => {
    if (typeof n === 'string' && n) taken.add(norm(n))
  })

  const first = `${baseName} - ${copyToken}`
  if (!taken.has(norm(first))) return first

  const candidate = Array
    .from({ length: 998 }, (_, i) => `${baseName} - ${copyToken} ${i + 2}`)
    .find((c) => !taken.has(norm(c)))
  return candidate ?? `${baseName} - ${copyToken} 1000`
}
